// @LTG-ID: F-092
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: 08_FEATURES.md#F-092, source `Journals/what-is-my-business-worth.html`
//        lines 1017-1064 (Figure 02 — SDE-to-EBITDA inflection curve).
// @CHANGED: 2026-02 session_004 (CHG-0059)
// @OWNER: web
//
// F-082 — Brief-page infographic: "The inflection point — where the
// conversation flips from SDE to EBITDA." Verbatim port of source Figure 02.
// 720x280 viewBox SVG with two gradient fills (navy SDE region, gold EBITDA
// region), dashed teal inflection rule, and a one-decade horizontal scale
// from $0 to $50M+.

export const SdeToEbitdaInflection = () => {
    return (
        <div
            className="ltg-brief-infographic"
            data-testid="brief01-figure-02"
        >
            <span className="ltg-brief-infographic-label">
                Figure 02 &middot; The inflection point
            </span>
            <h4>
                Where the conversation flips from SDE to EBITDA, and why your
                buyer pool changes with it.
            </h4>
            <svg
                viewBox="0 0 720 280"
                className="ltg-brief-inflection-svg"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
            >
                <defs>
                    <linearGradient id="brief01-sdeFill" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#0A2A5E" stopOpacity="0.85" />
                        <stop offset="60%" stopColor="#0A2A5E" stopOpacity="0.4" />
                        <stop offset="100%" stopColor="#0A2A5E" stopOpacity="0" />
                    </linearGradient>
                    <linearGradient id="brief01-ebitdaFill" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#B08D3E" stopOpacity="0" />
                        <stop offset="40%" stopColor="#B08D3E" stopOpacity="0.5" />
                        <stop offset="100%" stopColor="#B08D3E" stopOpacity="0.85" />
                    </linearGradient>
                </defs>

                <line x1="40" y1="200" x2="700" y2="200" stroke="#d8d3c6" strokeWidth="1" />
                <line x1="40" y1="60" x2="40" y2="200" stroke="#d8d3c6" strokeWidth="1" />

                <text
                    x="40"
                    y="50"
                    fontFamily="Geist Mono, monospace"
                    fontSize="10"
                    fill="#6a7a92"
                    letterSpacing="1.2"
                >
                    BUYER POOL COMPOSITION
                </text>

                <path
                    d="M 40 80 L 280 80 L 360 130 L 700 130 L 700 200 L 40 200 Z"
                    fill="url(#brief01-sdeFill)"
                />
                <path
                    d="M 40 200 L 40 130 L 280 130 L 360 80 L 700 80 L 700 200 Z"
                    fill="url(#brief01-ebitdaFill)"
                />

                <line
                    x1="320"
                    y1="60"
                    x2="320"
                    y2="210"
                    stroke="#18B0BD"
                    strokeWidth="1.5"
                    strokeDasharray="4,3"
                />
                <text
                    x="320"
                    y="55"
                    fontFamily="Geist Mono, monospace"
                    fontSize="10"
                    fill="#18B0BD"
                    textAnchor="middle"
                    fontWeight="600"
                    letterSpacing="1"
                >
                    INFLECTION
                </text>
                <text
                    x="320"
                    y="225"
                    fontFamily="Source Serif 4, serif"
                    fontSize="13"
                    fill="#0A2A5E"
                    textAnchor="middle"
                    fontStyle="italic"
                >
                    $1M to $2M EBITDA
                </text>

                <text
                    x="160"
                    y="105"
                    fontFamily="Source Serif 4, serif"
                    fontSize="14"
                    fill="white"
                    textAnchor="middle"
                    fontWeight="600"
                >
                    SDE
                </text>
                <text
                    x="160"
                    y="125"
                    fontFamily="Source Serif 4, serif"
                    fontSize="11"
                    fill="white"
                    textAnchor="middle"
                    opacity="0.85"
                >
                    individuals, SBA, search funds
                </text>

                <text
                    x="540"
                    y="105"
                    fontFamily="Source Serif 4, serif"
                    fontSize="14"
                    fill="white"
                    textAnchor="middle"
                    fontWeight="600"
                >
                    EBITDA
                </text>
                <text
                    x="540"
                    y="125"
                    fontFamily="Source Serif 4, serif"
                    fontSize="11"
                    fill="white"
                    textAnchor="middle"
                    opacity="0.85"
                >
                    PE, family offices, strategics
                </text>

                <text x="40" y="260" fontFamily="Geist Mono, monospace" fontSize="10" fill="#6a7a92" letterSpacing="0.8">$0</text>
                <text x="200" y="260" fontFamily="Geist Mono, monospace" fontSize="10" fill="#6a7a92" textAnchor="middle" letterSpacing="0.8">$500K</text>
                <text x="320" y="260" fontFamily="Geist Mono, monospace" fontSize="10" fill="#6a7a92" textAnchor="middle" letterSpacing="0.8">$1M</text>
                <text x="440" y="260" fontFamily="Geist Mono, monospace" fontSize="10" fill="#6a7a92" textAnchor="middle" letterSpacing="0.8">$2M</text>
                <text x="580" y="260" fontFamily="Geist Mono, monospace" fontSize="10" fill="#6a7a92" textAnchor="middle" letterSpacing="0.8">$5M</text>
                <text x="700" y="260" fontFamily="Geist Mono, monospace" fontSize="10" fill="#6a7a92" textAnchor="end" letterSpacing="0.8">$50M+</text>
                <text x="360" y="275" fontFamily="Geist Mono, monospace" fontSize="10" fill="#6a7a92" textAnchor="middle" letterSpacing="0.8">NORMALIZED EARNINGS</text>
            </svg>
            <p className="ltg-brief-infographic-caption">
                Below $1M to $2M of normalized earnings, the buyer pool is
                dominated by individual operators who underwrite to SDE. Above
                it, the pool shifts to private equity and strategics who
                underwrite to EBITDA with a market-rate manager assumed. Same
                business, different earnings number, different buyer.
                <cite>
                    Source: synthesis of IBBA Market Pulse Q4 2025, BizBuySell
                    2025 Year-End, GF Data Resources Q1 2025
                </cite>
            </p>
        </div>
    );
};

export default SdeToEbitdaInflection;
